import React from "react";
import { Icon } from "../../UIElements/Icon";
import StatusBadge from "../../Data/StatusBadge";
import { useGlobalUtils } from "../../../../lib/globalUtils";
import { useTranslation } from "react-i18next";
import {
  getStatusIconColor,
  getIndividualStatusColor,
} from "@finitless/shared/orderConstants";

/**
 * OrderRow component for displaying individual orders
 * @param {Object} props - Component props
 * @param {Object} props.order - Order data object
 * @param {number} props.index - Index of the order in the list
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onClick - Click handler function
 */
const OrderRow = ({ order, index, className = "", onClick }) => {
  const { t } = useTranslation("orders");
  const { parsePrice } = useGlobalUtils();

  const channelIcons = {
    WhatsApp: "MessageCircle",
    "Web Chat": "MessageSquare",
    "Phone AI": "Bot",
    Phone: "Phone",
  };

  const channelColors = {
    WhatsApp: "text-green-400",
    "Web Chat": "text-blue-400",
    "Phone AI": "text-purple-400",
    Phone: "text-cyan-400",
  };

  const customer =
    [order?.customer?.name, order?.customer?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() || t("row.customerFallback", { index: index + 1 });

  const processedOrder = {
    id: order.id || `#${String(index + 1).padStart(4, "0")}`,
    customer,
    total: order.summary?.total || 0,
    status: order.status,
    timestamp: new Date(
      order.created_at?.endsWith("Z")
        ? order.created_at
        : order.created_at + "Z",
    ),
  };

  const getIconColor = (status) => {
    // Use centralized color system
    return getIndividualStatusColor(status) || getStatusIconColor(status);
  };

  const channelIcon = channelIcons[processedOrder.channel] || "Minus";
  const channelColor =
    channelColors[processedOrder.channel] ||
    getIconColor(processedOrder.status);

  return (
    <div
      className={`flex items-center justify-between border-b border-gray-800 p-2 last:border-b-0 sm:p-4 ${onClick ? "cursor-pointer transition-colors duration-200 hover:bg-gray-800/50" : ""} ${className}`}
      onClick={onClick ? () => onClick(order) : undefined}
    >
      <div className="flex min-w-0 flex-1 items-center space-x-2 sm:space-x-4">
        <div className={`${channelColor} flex-shrink-0`}>
          <Icon name={channelIcon} size={16} className="sm:h-5 sm:w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-white sm:text-base">
            {processedOrder.id}
          </p>
          <p className="truncate text-xs text-gray-400 sm:text-sm">
            {processedOrder.customer}{" "}
            {processedOrder.channel ? `• ${processedOrder.channel}` : ""}
          </p>
        </div>
      </div>
      <div className="flex flex-shrink-0 items-center space-x-2 sm:space-x-4">
        <div className="text-right">
          <p className="text-sm font-medium text-white sm:text-base">
            {parsePrice(processedOrder.total)}
          </p>
          <p className="text-xs text-gray-400">
            {processedOrder.timestamp.toLocaleDateString([], {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}{" "}
            {processedOrder.timestamp.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
        <StatusBadge status={processedOrder.status} showIcon={false} />
      </div>
    </div>
  );
};

export default OrderRow;
